import React from 'react'
import { Button, Flex, H2, Line } from '../../lib/components'
import type { FaqFlatRow } from '../../services/faq-service'
import './faq-list.scss'

export type FaqListPresenterProps = {
  items: FaqFlatRow[]
  isLoading: boolean
  onAdd: () => void
  onEdit: (id: number) => void
  onDelete: (id: number) => void
}

function truncate(text: string, maxLen: number) {
  if (text.length <= maxLen) return text
  return `${text.slice(0, maxLen)}…`
}

export function FaqListPresenter({
  items,
  isLoading,
  onAdd,
  onEdit,
  onDelete,
}: FaqListPresenterProps) {
  return (
    <Flex variant='column-center'>
      <div className='faq-admin-list w-100'>
        <div className='faq-admin-list__header'>
          <H2 text='FAQs' />
          <Button
            text='Add FAQ'
            variant='basic-btn'
            handleClick={onAdd}
            classes={['faq-admin-list__add-btn']}
            dataTestId='faq-list-add'
          />
        </div>
        <Line />
        {isLoading ? (
          <p className='faq-admin-list__loading'>Loading…</p>
        ) : items.length === 0 ? (
          <p className='faq-admin-list__empty'>
            No FAQs yet. Use Add FAQ to create entries shown on the client site.
          </p>
        ) : (
          <div className='faq-admin-list__table-wrap'>
            <table className='faq-admin-list__table'>
              <colgroup>
                <col style={{ width: '56px' }} />
                <col style={{ width: '18%' }} />
                <col />
                <col style={{ width: '72px' }} />
                <col style={{ width: '188px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope='col'>ID</th>
                  <th scope='col'>Category</th>
                  <th scope='col'>Question</th>
                  <th scope='col'>Sort</th>
                  <th scope='col' className='faq-admin-list__th--actions'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td className='faq-admin-list__cell--id'>{row.id}</td>
                    <td className='faq-admin-list__cell--category'>{row.category ?? '—'}</td>
                    <td className='faq-admin-list__cell--question'>
                      {truncate(row.question, 120)}
                    </td>
                    <td className='faq-admin-list__cell--sort'>{row.sort_order}</td>
                    <td>
                      <div className='faq-admin-list__actions'>
                        <Button
                          text='Edit'
                          variant='secondary-btn'
                          handleClick={() => onEdit(row.id)}
                          classes={['faq-admin-list__action-btn']}
                          dataTestId={`faq-edit-${row.id}`}
                        />
                        <Button
                          text='Delete'
                          variant='secondary-btn'
                          handleClick={() => onDelete(row.id)}
                          classes={['faq-admin-list__action-btn']}
                          dataTestId={`faq-delete-${row.id}`}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Flex>
  )
}
